'use client';

import { useState } from 'react';
import Link from 'next/link';
import { MARKETING_TOKENS as T } from '@/components/marketing/marketingTokens';
import {
  COST_TIERS, LAND_UNITS, DEFAULT_AREA_SQFT,
  computeCost, inr, inrShort, tierByKey,
} from '@/lib/tools/constructionCost';
import { demoHref } from '@/lib/contactHref';

const SERIF = 'var(--font-serif), Georgia, "Times New Roman", serif';
const MONO = 'var(--f-mono), ui-monospace, SFMono-Regular, Menlo, monospace';

/**
 * The interactive half of the cost calculator.
 *
 * ── Why the page is split this way ──────────────────────────────────────────
 * Everything a crawler needs — the h1, the explanation, the cost-head table,
 * the FAQ — is server-rendered by page.tsx. This island is only the part that
 * has to react to typing. That split is not a style preference: an auth-gated
 * or fully client-rendered marketing page server-renders as an empty shell,
 * and nineteen pages on this site once shipped that way and were indexed as
 * blank. A tool page is the most tempting place to repeat that mistake,
 * because the tool genuinely is interactive.
 *
 * ── Why the rate is an input and not a result ───────────────────────────────
 * The defaults are a band we cannot stand behind as a quote, so the UI treats
 * them as what they are: a starting point the reader is expected to replace.
 * Changing the tier re-seeds the rate; typing in the rate box wins from then
 * on, and the page says which of those two is currently in force. A calculator
 * that quietly overwrites what the user typed teaches them not to trust it.
 */
export default function CostCalculator() {
  const [tierKey, setTierKey] = useState<string>('standard');
  const [rate, setRate] = useState<number>(tierByKey('standard').low);
  const [rateTouched, setRateTouched] = useState(false);
  const [unit, setUnit] = useState<string>('sqft');
  const [areaRaw, setAreaRaw] = useState<string>(String(DEFAULT_AREA_SQFT));

  const tier = tierByKey(tierKey);
  const unitDef = LAND_UNITS.find((u) => u.key === unit) ?? LAND_UNITS[0];
  const areaSqft = (parseFloat(areaRaw) || 0) * unitDef.sqft;
  const { total, heads } = computeCost({ areaSqft, ratePerSqft: rate });

  // Selecting a tier re-seeds the rate, but never clobbers a rate the reader
  // typed — that edit is the whole point of the page.
  const pickTier = (k: string) => {
    setTierKey(k);
    if (!rateTouched) setRate(tierByKey(k).low);
  };

  const band = { low: Math.round(areaSqft * tier.low), high: Math.round(areaSqft * tier.high) };

  return (
    <div className="flex flex-col gap-5">
      {/* ── Inputs ─────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-5 p-5 md:p-6" style={{ border: `1px solid ${T.BORDER}`, backgroundColor: T.SURFACE, borderRadius: 4 }}>
        <div className="flex flex-col gap-2">
          <label htmlFor="bsn-area" className="text-[13px] font-semibold uppercase tracking-[0.12em]" style={{ color: T.TEXT_MUTED }}>
            Built-up area
          </label>
          <div className="flex flex-wrap gap-2">
            <input
              id="bsn-area"
              type="number"
              inputMode="decimal"
              min={0}
              value={areaRaw}
              onChange={(e) => setAreaRaw(e.target.value)}
              className="min-w-0 flex-1 px-3 py-2.5 text-[16px]"
              style={{ border: `1px solid ${T.BORDER}`, borderRadius: 4, color: T.TEXT, backgroundColor: T.BG, fontFamily: MONO }}
            />
            <select
              aria-label="Area unit"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              // 16px, not 15: iOS Safari zooms the viewport when a form
              // control under 16px takes focus, and the Aug 2026 mobile audit
              // drove 180 of those to zero across the app. This is a page for
              // typing numbers on a phone at a site — it cannot be the one
              // that brings the problem back.
              className="px-3 py-2.5 text-[16px]"
              style={{ border: `1px solid ${T.BORDER}`, borderRadius: 4, color: T.TEXT, backgroundColor: T.BG }}
            >
              {LAND_UNITS.map((u) => <option key={u.key} value={u.key}>{u.label}</option>)}
            </select>
          </div>
          <p className="text-[13.5px]" style={{ color: T.TEXT_SOFT }}>
            {unit === 'sqft'
              ? 'Total constructed area across all floors — not the plot area.'
              : `${unitDef.note}. That is ${inr(areaSqft)} sq.ft of built-up area.`}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-[13px] font-semibold uppercase tracking-[0.12em]" style={{ color: T.TEXT_MUTED }}>
            Finish level
          </span>
          <div className="flex flex-wrap gap-2">
            {COST_TIERS.map((t) => {
              const on = t.key === tierKey;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => pickTier(t.key)}
                  aria-pressed={on}
                  className="px-4 py-2 text-[14.5px] font-semibold"
                  style={{
                    borderRadius: 999,
                    border: `1px solid ${on ? T.PRIMARY : T.BORDER}`,
                    backgroundColor: on ? T.PRIMARY : T.BG,
                    color: on ? T.ON_PRIMARY : T.TEXT,
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
          <p className="text-[13.5px]" style={{ color: T.TEXT_SOFT }}>{tier.blurb}</p>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="bsn-rate" className="text-[13px] font-semibold uppercase tracking-[0.12em]" style={{ color: T.TEXT_MUTED }}>
            Rate — ₹ per sq.ft
          </label>
          <input
            id="bsn-rate"
            type="number"
            inputMode="decimal"
            min={0}
            value={rate}
            onChange={(e) => { setRate(parseFloat(e.target.value) || 0); setRateTouched(true); }}
            className="w-full px-3 py-2.5 text-[16px]"
            style={{ border: `1px solid ${T.BORDER}`, borderRadius: 4, color: T.TEXT, backgroundColor: T.BG, fontFamily: MONO }}
          />
          <p className="text-[13.5px]" style={{ color: T.TEXT_SOFT }}>
            {rateTouched
              ? 'Using your rate. This is the number that makes the result worth anything.'
              : `Seeded from the ${tier.label.toLowerCase()} band (₹${inr(tier.low)}–₹${inr(tier.high)}). Replace it with your own.`}
          </p>
        </div>
      </div>

      {/* ── Result ─────────────────────────────────────────────────────── */}
      <div className="p-5 md:p-6" style={{ border: `1px solid ${T.BORDER}`, backgroundColor: T.SURFACE_ALT || T.SURFACE, borderRadius: 4 }}>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <span className="text-[13px] font-semibold uppercase tracking-[0.12em]" style={{ color: T.TEXT_MUTED }}>
            Indicative build cost
          </span>
          <span className="text-[13.5px]" style={{ color: T.TEXT_SOFT, fontFamily: MONO }}>
            {inr(areaSqft)} sq.ft × ₹{inr(rate)}
          </span>
        </div>
        <div className="mt-1 text-[34px] md:text-[44px]" style={{ fontFamily: SERIF, fontWeight: 600, letterSpacing: '-0.02em', color: T.TEXT }}>
          {inrShort(total)}
        </div>
        <p className="mt-1 text-[14px]" style={{ color: T.TEXT_MUTED, fontFamily: MONO }}>₹{inr(total)}</p>
        {!rateTouched && areaSqft > 0 && (
          <p className="mt-3 text-[14px]" style={{ color: T.TEXT_MUTED }}>
            At the full {tier.label.toLowerCase()} band this job lands between{' '}
            <strong style={{ color: T.TEXT }}>{inrShort(band.low)}</strong> and{' '}
            <strong style={{ color: T.TEXT }}>{inrShort(band.high)}</strong>. The spread is the point: it is
            the difference between a specification and a guess.
          </p>
        )}

        <div className="mt-5 flex flex-col" style={{ borderTop: `1px solid ${T.BORDER_SOFT}` }}>
          {heads.map((h) => (
            <div key={h.key} className="flex items-center justify-between gap-4 py-2.5" style={{ borderBottom: `1px solid ${T.BORDER_SOFT}` }}>
              <span className="min-w-0 text-[15px]" style={{ color: T.TEXT }}>
                {h.label}
                <span className="ml-2 text-[13px]" style={{ color: T.TEXT_SOFT, fontFamily: MONO }}>{h.share}%</span>
              </span>
              <span className="shrink-0 text-[15px]" style={{ color: T.TEXT, fontFamily: MONO }}>₹{inr(h.amount)}</span>
            </div>
          ))}
        </div>

        <p className="mt-4 text-[13.5px] leading-relaxed" style={{ color: T.TEXT_SOFT }}>
          Excludes land, compound wall, borewell, EB connection and deposits, approval and liaison fees,
          interiors, landscaping and GST. Those are quoted separately on almost every job.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Link
          href={demoHref('tool-cost-calculator')}
          className="inline-flex items-center justify-center px-6 py-3 text-[15px] font-semibold no-underline"
          style={{ borderRadius: 999, background: T.PRIMARY, color: T.ON_PRIMARY }}
        >
          Turn this into a real quotation
        </Link>
        <Link
          href="/modules/estimates"
          className="inline-flex items-center justify-center px-6 py-3 text-[15px] font-semibold no-underline"
          style={{ borderRadius: 999, color: T.TEXT, border: `1px solid ${T.BORDER}`, backgroundColor: T.SURFACE }}
        >
          How estimating works
        </Link>
      </div>
    </div>
  );
}
